/**
 * WeeklyTimesheetModal Component
 * 
 * Harvest-style weekly timesheet entry interface.
 * Features:
 * - Week view: Sunday–Saturday for CA employees, Monday–Sunday for US employees
 * - Employee autocomplete with typeahead
 * - Op code selection with description
 * - Auto-save as draft
 * - Submit button to finalize
 * - Multiple rows per week
 * - RnB, Meal$, Travel$ entered once per day in a shared Daily Allowances section
 *   and distributed proportionally across project rows by hours at save time
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { required, validateSchema, isValid } from '../../utils/validation';
import EmployeeAutocomplete from '../common/EmployeeAutocomplete';
import OpCodeDescriptionSelect from '../common/OpCodeDescriptionSelect';
import GroupedProjectSelect from '../common/GroupedProjectSelect';
import { useToast } from '../../hooks/useToast';
import TimeTrackerHelpModal from './TimeTrackerHelpModal';
import { buildProjectDisciplineOptions } from './projectDisciplineOptions';

import { toLocalDateString, addDaysToDateString, getWeekDateStrings } from '../../utils/timesheetDates';

const formatDateHeader = (dateInfo) => {
  const days   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return { day: days[dateInfo.dayOfWeek], date: dateInfo.dayNum, month: months[dateInfo.month], full: dateInfo.dateStr };
};

/**
 * Distribute RnB, Meal$, Travel$ across rows proportionally by hours (reg+t12+dbl).
 * Rounding remainder goes to the highest-hours row. Zero hours → zero allowances.
 */
const distributeAllowances = (rows, totalRnb, totalMeals, totalTravel) => {
  const totalHours = rows.reduce(
    (s, r) => s + (parseFloat(r.reg) || 0) + (parseFloat(r.t12) || 0) + (parseFloat(r.dbl) || 0),
    0
  );
  if (totalHours === 0) {
    return rows.map(r => ({ ...r, rnb: 0, meals: 0, travel_2: 0 }));
  }
  const distributed = rows.map(r => {
    const hrs  = (parseFloat(r.reg) || 0) + (parseFloat(r.t12) || 0) + (parseFloat(r.dbl) || 0);
    const frac = hrs / totalHours;
    return {
      ...r,
      rnb:      Math.round(totalRnb    * frac * 100) / 100,
      meals:    Math.round(totalMeals  * frac * 100) / 100,
      travel_2: Math.round(totalTravel * frac * 100) / 100,
    };
  });
  ['rnb', 'meals', 'travel_2'].forEach(key => {
    const total = { rnb: totalRnb, meals: totalMeals, travel_2: totalTravel }[key];
    const diff  = Math.round((total - distributed.reduce((s, r) => s + r[key], 0)) * 100) / 100;
    if (diff !== 0) {
      const maxIdx = distributed.reduce((mi, r, i) => {
        const h  = (parseFloat(r.reg) || 0) + (parseFloat(r.t12) || 0) + (parseFloat(r.dbl) || 0);
        const mh = (parseFloat(distributed[mi].reg) || 0) + (parseFloat(distributed[mi].t12) || 0) + (parseFloat(distributed[mi].dbl) || 0);
        return h > mh ? i : mi;
      }, 0);
      distributed[maxIdx][key] = Math.round((distributed[maxIdx][key] + diff) * 100) / 100;
    }
  });
  return distributed;
};

/**
 * Build API entries with allowances distributed per day across project rows.
 * Used by both autoSave and handleSubmit.
 */
const buildEntriesWithAllowances = (currentRows, currentDailyAllowances, currentWeekDates, employee) => {
  const allEntries = [];
  [0, 1, 2, 3, 4, 5, 6].forEach(dayIdx => {
    const dateInfo    = currentWeekDates[dayIdx];
    if (!dateInfo) return;
    const dayAllowance = currentDailyAllowances[dayIdx] || { rnb: 0, meals: 0, travel_2: 0 };

    // Rows that have at least one hour type for this day
    const rowsWithHours = currentRows
      .filter(row => row.op_code && row.project_id)
      .filter(row => {
        const d = row.days[dayIdx];
        return d && (d.reg || d.t12 || d.dbl || d.height);
      });

    if (rowsWithHours.length === 0) return;

    // Lightweight objects for distributeAllowances (only reg/t12/dbl needed for fractions)
    const distInputs = rowsWithHours.map(row => ({
      reg: row.days[dayIdx].reg,
      t12: row.days[dayIdx].t12,
      dbl: row.days[dayIdx].dbl,
    }));

    const distributed = distributeAllowances(
      distInputs,
      parseFloat(dayAllowance.rnb)      || 0,
      parseFloat(dayAllowance.meals)    || 0,
      parseFloat(dayAllowance.travel_2) || 0
    );

    rowsWithHours.forEach((row, i) => {
      const dayData        = row.days[dayIdx];
      const disciplineCode = row.optionKey ? row.optionKey.split('-')[1] : null;
      const effectiveShift = dayData.shift || row.shift;
      allEntries.push({
        id:                  dayData.id || null,
        project_id:          row.project_id,
        discipline_code:     disciplineCode,
        date:                dateInfo.dateStr,
        empl_code:           employee.emp_code,
        empl_name:           employee.emp_name,
        trade:               employee.trade,
        division:            employee.division,
        op_code:             row.op_code,
        op_code_description: row.op_code_description,
        reg:                 parseFloat(dayData.reg)    || 0,
        t12:                 parseFloat(dayData.t12)    || 0,
        dbl:                 parseFloat(dayData.dbl)    || 0,
        height:              parseFloat(dayData.height) || 0,
        rnb:                 distributed[i].rnb,
        meals:               distributed[i].meals,
        travel_2:            distributed[i].travel_2,
        shift:               effectiveShift,
        comment:             dayData.comment || null,
      });
    });
  });
  return allEntries;
};

// Empty row template (rnb/meals/travel_2 kept in days model for load reconstruction)
const createEmptyRow = (defaultProjectId = null, defaultJobNumber = null, defaultOptionKey = null) => ({
  id: null,
  project_id:          defaultProjectId,
  job_number:          defaultJobNumber,
  optionKey:           defaultOptionKey,
  op_code:             '',
  op_code_description: '',
  shift: 'D',
  days: {
    0: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, comment: '' },
    1: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, comment: '' },
    2: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, comment: '' },
    3: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, comment: '' },
    4: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, comment: '' },
    5: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, comment: '' },
    6: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, comment: '' },
  }
});

const emptyDailyAllowances = () =>
  Array.from({ length: 7 }, () => ({ rnb: '', meals: '', travel_2: '' }));

const WeeklyTimesheetModal = ({
  projectId,
  jobNumber,
  onClose,
  onSubmitComplete,
  user,
  isElevated = false
}) => {
  const { instance, accounts } = useMsal();
  const [weekStartStr, setWeekStartStr] = useState(() => toLocalDateString(new Date()));
  const [weekDates, setWeekDates]       = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [rows, setRows]   = useState([createEmptyRow()]);
  const [loading, setLoading]           = useState(false);
  const [projects, setProjects]         = useState([]);
  const [loadingCurrentUser, setLoadingCurrentUser] = useState(true);
  const [saving, setSaving]             = useState(false);
  const [showHelp, setShowHelp]         = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [copyingLastWeek, setCopyingLastWeek] = useState(false);
  // dailyAllowances[0..6] = { rnb, meals, travel_2 } — one total per day
  const [dailyAllowances, setDailyAllowances] = useState(emptyDailyAllowances);
  // Pre-calculated per-row distribution (display only): { [dayIdx]: { [rowIndex]: { rnb, meals, travel_2 } } }
  const [computedRowAllowances, setComputedRowAllowances] = useState({});
  const { toast, showToast } = useToast();

  const saveTimeoutRef              = useRef(null);
  const rowsRef                     = useRef(rows);
  const pendingSaveRef              = useRef(false);
  const dailyAllowancesRef          = useRef(dailyAllowances);
  const weekDatesRef                = useRef(weekDates);
  const computedRowAllowancesRef    = useRef({});
  const isDirtyRef                  = useRef(false); // tracks unsaved changes

  // Keep refs in sync
  useEffect(() => { rowsRef.current                 = rows;                 }, [rows]);
  useEffect(() => { dailyAllowancesRef.current       = dailyAllowances;      }, [dailyAllowances]);
  useEffect(() => { weekDatesRef.current             = weekDates;            }, [weekDates]);
  useEffect(() => { computedRowAllowancesRef.current = computedRowAllowances; }, [computedRowAllowances]);

  const currentUserEmail = accounts[0]?.username;

  // 0 = Sunday (CA / unassigned), 1 = Monday (US)
  const weekStartDay = useMemo(
    () => (selectedEmployee?.payroll_group === 'US' ? 1 : 0),
    [selectedEmployee]
  );

  useEffect(() => { setWeekDates(getWeekDateStrings(weekStartStr, weekStartDay)); }, [weekStartStr, weekStartDay]);

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/projects');
        if (response.data.success || response.data.projects) {
          const sorted = [...(response.data.projects || [])].sort((a, b) => {
            const numA = parseInt(a.job_number, 10);
            const numB = parseInt(b.job_number, 10);
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return (a.job_number || '').localeCompare(b.job_number || '');
          });
          setProjects(sorted);
        }
      } catch (error) { console.error('Error fetching projects:', error); }
    };
    fetchProjects();
  }, [instance, accounts]);

  const projectDisciplineOptions = useMemo(
    () => buildProjectDisciplineOptions(projects),
    [projects]
  );

  // Auto-populate current user's employee
  useEffect(() => {
    const fetchCurrentUserEmployee = async () => {
      if (!currentUserEmail) { setLoadingCurrentUser(false); return; }
      try {
        const response = await authenticatedApiCall(
          instance, accounts, 'GET',
          `/lt-employee/by-email?email=${encodeURIComponent(currentUserEmail)}`
        );
        if (response.data.success && response.data.data) {
          const emp = response.data.data;
          setSelectedEmployee({ emp_code: emp.emp_code, emp_name: emp.emp_name, empl_code: emp.emp_code, empl_name: emp.emp_name, trade: emp.trade, division: emp.division, payroll_group: emp.payroll_group ?? null });
        }
      } catch (error) { console.error('Error fetching current user employee:', error); }
      finally { setLoadingCurrentUser(false); }
    };
    fetchCurrentUserEmployee();
  }, [instance, accounts, currentUserEmail]);

  const goToPreviousWeek = () => setWeekStartStr(addDaysToDateString(weekStartStr, -7));
  const goToNextWeek     = () => setWeekStartStr(addDaysToDateString(weekStartStr,  7));

  // Load existing entries and reconstruct dailyAllowances by summing per day
  const loadWeekData = useCallback(async () => {
    if (!selectedEmployee?.emp_code) return;
    setLoading(true);
    try {
      const startStr = weekDates.length > 0 ? weekDates[0].dateStr : weekStartStr;
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/actual-spent/week/${startStr}?projectId=${projectId}&empCode=${selectedEmployee.emp_code}`
      );

      if (response.data.success && response.data.data.length > 0) {
        const grouped = {};
        response.data.data.forEach(entry => {
          const groupKey = `${entry.op_code || 'no_opcode'}_${entry.project_id}_${entry.discipline_code || ''}`;
          if (!grouped[groupKey]) {
            const disciplineCode = entry.discipline_code || '';
            const optionKey = entry.project_id && disciplineCode ? `${entry.project_id}-${disciplineCode}` : null;
            grouped[groupKey] = {
              id: null, project_id: entry.project_id, job_number: entry.job_number,
              optionKey, op_code: entry.op_code, op_code_description: entry.op_code_description,
              shift: entry.shift || 'D',
              days: {
                0: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, id: null, comment: '' },
                1: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, id: null, comment: '' },
                2: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, id: null, comment: '' },
                3: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, id: null, comment: '' },
                4: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, id: null, comment: '' },
                5: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, id: null, comment: '' },
                6: { reg: '', t12: '', dbl: '', height: '', rnb: '', meals: '', travel_2: '', shift: null, id: null, comment: '' },
              }
            };
          }
          const [year, month, day] = entry.date.split('T')[0].split('-').map(Number);
          const entryDate  = new Date(year, month - 1, day, 12, 0, 0);
          const dayIndex   = entryDate.getDay();
          const rowShift   = grouped[groupKey].shift;
          const dayShift   = entry.shift && entry.shift !== rowShift ? entry.shift : null;
          grouped[groupKey].days[dayIndex] = {
            reg: entry.reg || '', t12: entry.t12 || '', dbl: entry.dbl || '',
            height: entry.height || '', rnb: entry.r_and_b || '',
            meals: entry.meals || '', travel_2: entry.travel_2 || '',
            shift: dayShift, id: entry.id,
            comment: entry.comment || '',
          };
        });

        const loadedRows = Object.values(grouped);

        // Reconstruct daily allowance totals by summing distributed values across rows
        const reconstructed = Array.from({ length: 7 }, () => ({ rnb: 0, meals: 0, travel_2: 0 }));
        loadedRows.forEach(row => {
          Object.entries(row.days).forEach(([dayIdx, day]) => {
            const i = parseInt(dayIdx);
            reconstructed[i].rnb      += parseFloat(day.rnb)      || 0;
            reconstructed[i].meals    += parseFloat(day.meals)    || 0;
            reconstructed[i].travel_2 += parseFloat(day.travel_2) || 0;
          });
        });
        setDailyAllowances(reconstructed.map(a => ({
          rnb:      a.rnb      > 0 ? a.rnb.toFixed(2)      : '',
          meals:    a.meals    > 0 ? a.meals.toFixed(2)    : '',
          travel_2: a.travel_2 > 0 ? a.travel_2.toFixed(2) : '',
        })));

        setRows(loadedRows.length > 0 ? loadedRows : [createEmptyRow()]);
      } else {
        setRows([createEmptyRow()]);
        setDailyAllowances(emptyDailyAllowances());
      }
    } catch (error) {
      console.error('Error loading week data:', error);
      showToast('Failed to load existing entries', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId, weekStartStr, weekDates, selectedEmployee]);

  useEffect(() => {
    if (selectedEmployee?.emp_code) loadWeekData();
  }, [selectedEmployee, weekStartStr, loadWeekData]);

  // Auto-save — reads from refs to avoid stale closure
  const autoSave = useCallback(async () => {
    if (!selectedEmployee?.emp_code) return;
    if (saving) { pendingSaveRef.current = true; return; }
    pendingSaveRef.current = false;
    setSaving(true);
    try {
      const entries = buildEntriesWithAllowances(
        rowsRef.current,
        dailyAllowancesRef.current,
        weekDatesRef.current,
        selectedEmployee
      );
      if (entries.length > 0) {
        const response = await authenticatedApiCall(
          instance, accounts, 'POST', '/actual-spent/week-entry', { entries }
        );
        if (response.data.success) {
          isDirtyRef.current = false;
          const savedEntries = response.data.data.saved || [];
          if (savedEntries.length > 0) {
            setRows(prev => {
              const updated = prev.map(row => ({ ...row, days: { ...row.days } }));
              savedEntries.forEach(saved => {
                const rowIndex = updated.findIndex(r => r.op_code === saved.op_code && r.project_id === saved.project_id);
                if (rowIndex >= 0) {
                  const [year, month, day] = saved.date.split('T')[0].split('-').map(Number);
                  const savedDate = new Date(year, month - 1, day, 12, 0, 0);
                  const dayIndex  = savedDate.getDay();
                  updated[rowIndex].days = {
                    ...updated[rowIndex].days,
                    [dayIndex]: { ...updated[rowIndex].days[dayIndex], id: saved.id }
                  };
                }
              });
              return updated;
            });
          }
        }
      }
    } catch (error) {
      console.error('Auto-save error:', error);
      showToast('Auto-save failed — your changes may not be saved. Use "Save Draft" to retry.', 'error');
    } finally {
      setSaving(false);
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        setTimeout(() => autoSave(), 100);
      }
    }
  }, [instance, accounts, selectedEmployee, saving]);

  const triggerAutoSave = () => {
    isDirtyRef.current = true;
    pendingSaveRef.current = true;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => autoSave(), 1500);
  };

  const handleCellChange = (rowIndex, dayIndex, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        days: { ...updated[rowIndex].days, [dayIndex]: { ...updated[rowIndex].days[dayIndex], [field]: value } }
      };

      // Per-day hour cap warning (REG+T12+DBL only, not height)
      if (['reg', 't12', 'dbl'].includes(field)) {
        const dayTotal = updated.reduce((s, r) =>
          s + (parseFloat(r.days[dayIndex].reg) || 0)
            + (parseFloat(r.days[dayIndex].t12) || 0)
            + (parseFloat(r.days[dayIndex].dbl) || 0), 0);
        if (dayTotal > 24) {
          setTimeout(() => showToast(`Day total exceeds 24 hours (${dayTotal.toFixed(1)}h) — please review.`, 'error'), 0);
        }
      }

      return updated;
    });
    triggerAutoSave();
  };

  const updateDailyAllowance = (dayIndex, field, value) => {
    isDirtyRef.current = true;
    setDailyAllowances(prev => {
      const updated = [...prev];
      updated[dayIndex] = { ...updated[dayIndex], [field]: value };
      return updated;
    });
    // Clear computed so row cells revert to "—" until recalculated
    setComputedRowAllowances({});
    triggerAutoSave();
  };

  const handleCalculateAllowances = () => {
    const computed = {};
    [0, 1, 2, 3, 4, 5, 6].forEach(dayIdx => {
      const dayAllowance = dailyAllowances[dayIdx] || { rnb: 0, meals: 0, travel_2: 0 };
      const rowsWithHoursIndexed = rows
        .map((row, origIdx) => ({ row, origIdx }))
        .filter(({ row }) => row.op_code && row.project_id)
        .filter(({ row }) => {
          const d = row.days[dayIdx];
          return d && (d.reg || d.t12 || d.dbl);
        });
      if (rowsWithHoursIndexed.length === 0) return;
      const distInputs = rowsWithHoursIndexed.map(({ row }) => ({
        reg: row.days[dayIdx].reg,
        t12: row.days[dayIdx].t12,
        dbl: row.days[dayIdx].dbl,
      }));
      const distributed = distributeAllowances(
        distInputs,
        parseFloat(dayAllowance.rnb)      || 0,
        parseFloat(dayAllowance.meals)    || 0,
        parseFloat(dayAllowance.travel_2) || 0
      );
      computed[dayIdx] = {};
      rowsWithHoursIndexed.forEach(({ origIdx }, i) => {
        computed[dayIdx][origIdx] = {
          rnb:      distributed[i].rnb,
          meals:    distributed[i].meals,
          travel_2: distributed[i].travel_2,
        };
      });
    });
    setComputedRowAllowances(computed);
  };

  const handleOpCodeChange = (rowIndex, opCodeData) => {
    setRows(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], op_code: opCodeData.op_code || '', op_code_description: opCodeData.op_description || opCodeData.op_code_description || '' };
      return updated;
    });
  };

  const handleRowProjectChange = (rowIndex, optionKey) => {
    const selected = projectDisciplineOptions.find(opt => opt.optionKey === optionKey);
    setRows(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], project_id: selected?.project_id || null, job_number: selected?.display_value || null, optionKey: optionKey || null, op_code: '', op_code_description: '' };
      return updated;
    });
  };

  const handleRowShiftChange = (rowIndex, shiftValue) => {
    setRows(prev => { const u = [...prev]; u[rowIndex] = { ...u[rowIndex], shift: shiftValue }; return u; });
  };

  const SHIFT_OPTIONS = ['D', 'A', 'N'];

  const toggleDayShift = (rowIndex, dayIndex) => {
    setRows(prev => {
      const updated = [...prev];
      const currentDayShift = updated[rowIndex].days[dayIndex].shift;
      const rowShift = updated[rowIndex].shift;
      let newDayShift;
      if (currentDayShift === null) {
        // Start cycling from the next option after the row default
        const nextIdx = (SHIFT_OPTIONS.indexOf(rowShift) + 1) % 3;
        newDayShift = SHIFT_OPTIONS[nextIdx];
      } else {
        const nextIdx = (SHIFT_OPTIONS.indexOf(currentDayShift) + 1) % 3;
        const next = SHIFT_OPTIONS[nextIdx];
        // Reset to null (row default) when cycling back to match the row shift
        newDayShift = next === rowShift ? null : next;
      }
      updated[rowIndex] = { ...updated[rowIndex], days: { ...updated[rowIndex].days, [dayIndex]: { ...updated[rowIndex].days[dayIndex], shift: newDayShift } } };
      return updated;
    });
  };

  const getEffectiveShift = (row, dayIndex) => row.days[dayIndex].shift || row.shift;

  // Only check hours (not allowances) since allowances are now per-day global
  const dayHasHours = (dayData) => dayData.reg || dayData.t12 || dayData.dbl || dayData.height;

  const addRow = () => {
    const defaultProject    = rows.length > 0 ? rows[0].project_id  : projectId;
    const defaultJobNumber  = rows.length > 0 ? rows[0].job_number  : jobNumber;
    const defaultOptionKey  = rows.length > 0 ? rows[0].optionKey   : null;
    setRows(prev => [...prev, createEmptyRow(defaultProject, defaultJobNumber, defaultOptionKey)]);
  };

  const removeRow = (rowIndex) => {
    if (rows.length <= 1) { showToast('Cannot remove the last row', 'warning'); return; }
    setRows(prev => prev.filter((_, i) => i !== rowIndex));
  };

  const handleCopyLastWeek = async () => {
    if (!selectedEmployee?.emp_code) return;
    // Compute last week's Sunday start
    const prevWeekStart = addDaysToDateString(weekDates.length > 0 ? weekDates[0].dateStr : weekStartStr, -7);
    setCopyingLastWeek(true);
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/actual-spent/week/${prevWeekStart}?projectId=${projectId || 'null'}&empCode=${selectedEmployee.emp_code}`
      );
      if (!response.data.success || response.data.data.length === 0) {
        showToast('No entries found for last week', 'error');
        return;
      }
      // Reconstruct project/op-code rows with zeroed hours
      const grouped = {};
      response.data.data.forEach(entry => {
        const disciplineCode = entry.discipline_code || '';
        const optionKey = entry.project_id && disciplineCode ? `${entry.project_id}-${disciplineCode}` : null;
        const groupKey = `${entry.op_code || ''}_${entry.project_id}_${disciplineCode}`;
        if (!grouped[groupKey]) {
          grouped[groupKey] = createEmptyRow(entry.project_id, entry.job_number, optionKey);
          grouped[groupKey].op_code = entry.op_code || '';
          grouped[groupKey].op_code_description = entry.op_code_description || '';
          grouped[groupKey].shift = entry.shift || 'D';
        }
      });
      const copiedRows = Object.values(grouped);
      if (copiedRows.length === 0) {
        showToast('No rows to copy from last week', 'error');
        return;
      }
      setRows(copiedRows);
      setDailyAllowances(emptyDailyAllowances());
      setComputedRowAllowances({});
      isDirtyRef.current = false;
      showToast(`Copied ${copiedRows.length} row${copiedRows.length !== 1 ? 's' : ''} from last week — fill in your hours.`, 'success');
    } catch (err) {
      console.error('Copy last week error:', err);
      showToast('Failed to load last week\'s data', 'error');
    } finally {
      setCopyingLastWeek(false);
    }
  };

  const handleClose = () => {
    if (isDirtyRef.current) {
      if (!window.confirm('You have unsaved changes. Close without saving?')) return;
    }
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    const empErrors = validateSchema(
      { employee: selectedEmployee?.emp_code },
      { employee: [required('Please select an employee first')] }
    );
    if (!isValid(empErrors)) { showToast(empErrors.employee, 'error'); return; }

    const rowsWithData = rows.filter(row => {
      const hasHours = Object.values(row.days).some(day => day.reg || day.t12 || day.dbl || day.height);
      return hasHours;
    });
    const hasRowErrors = rowsWithData.some(row => {
      const errs = validateSchema(
        { project_id: row.project_id, op_code: row.op_code },
        { project_id: [required()], op_code: [required()] }
      );
      return !isValid(errs);
    });
    const hasNoHours = !rows.some(row =>
      Object.values(row.days).some(day => (parseFloat(day.reg) || 0) + (parseFloat(day.t12) || 0) + (parseFloat(day.dbl) || 0) > 0)
    );
    if (hasRowErrors || hasNoHours) {
      showToast('Please complete all required fields before submitting', 'error'); return;
    }

    // Daily hour cap check (block submit if any day exceeds 24h)
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const dayTotal = rows.reduce((s, r) =>
        s + (parseFloat(r.days[dayIndex].reg) || 0)
          + (parseFloat(r.days[dayIndex].t12) || 0)
          + (parseFloat(r.days[dayIndex].dbl) || 0), 0);
      if (dayTotal > 24) {
        const dateInfo = weekDates[dayIndex];
        showToast(`${dateInfo ? dateInfo.dateStr : `Day ${dayIndex + 1}`} total exceeds 24 hours (${dayTotal.toFixed(1)}h). Please correct before submitting.`, 'error');
        return;
      }
    }

    setSubmitting(true);
    try {
      const entries = buildEntriesWithAllowances(rows, dailyAllowances, weekDates, selectedEmployee);

      if (entries.length > 0) {
        const saveResponse = await authenticatedApiCall(
          instance, accounts, 'POST', '/actual-spent/week-entry', { entries }
        );
        if (!saveResponse.data.success) {
          showToast('Failed to save entries before submission', 'error'); return;
        }
      }

      pendingSaveRef.current = false;

      const projectIds = [...new Set(rowsWithData.map(r => r.project_id).filter(Boolean))];
      const response = await authenticatedApiCall(
        instance, accounts, 'POST', '/actual-spent/submit-week',
        { projectIds, startDate: weekDates.length > 0 ? weekDates[0].dateStr : weekStartStr, empCode: selectedEmployee.emp_code }
      );

      if (response.data.success) {
        showToast(`Submitted ${response.data.data.count} entries`, 'success');
        if (onSubmitComplete) onSubmitComplete();
      } else {
        showToast(response.data.message || 'Submit failed', 'error');
      }
    } catch (error) {
      console.error('Submit error:', error);
      showToast('Failed to submit timesheet', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Row/day total helpers (hours only — no allowances)
  const getRowTotalByType = (row, type) =>
    Object.values(row.days).reduce((s, day) => s + (parseFloat(day[type]) || 0), 0);

  const getRowTotal = (row) => {
    let total = 0;
    Object.values(row.days).forEach(day => {
      total += (parseFloat(day.reg) || 0) + (parseFloat(day.t12) || 0) + (parseFloat(day.dbl) || 0);
    });
    return total.toFixed(1);
  };

  const getDayTotalByType = (dayIndex, type) =>
    rows.reduce((s, row) => s + (parseFloat(row.days[dayIndex][type]) || 0), 0);

  const getDayTotal = (dayIndex) => {
    let total = 0;
    rows.forEach(row => {
      total += (parseFloat(row.days[dayIndex].reg) || 0) +
               (parseFloat(row.days[dayIndex].t12) || 0) +
               (parseFloat(row.days[dayIndex].dbl) || 0);
    });
    return total.toFixed(1);
  };

  const getGrandTotalByType = (type) =>
    rows.reduce((s, row) => s + Object.values(row.days).reduce((ss, day) => ss + (parseFloat(day[type]) || 0), 0), 0);

  const getWeekTitle = () => {
    if (weekDates.length < 7) return '';
    const start = formatDateHeader(weekDates[0]);
    const end   = formatDateHeader(weekDates[6]);
    const year  = weekDates[0].dateStr.split('-')[0];
    return `${start.month} ${start.date} - ${end.month} ${end.date}, ${year}`;
  };

  const allRowsHaveOpCode = rows.every(row => row.op_code && row.op_code.trim() !== '');

  // Modal-level validation (employee selection)
  const modalErrors = useMemo(() => submitted ? validateSchema(
    { employee: selectedEmployee?.emp_code },
    { employee: [required('Employee is required')] }
  ) : {}, [submitted, selectedEmployee]);

  // Per-row validation errors (shown after first submit attempt)
  const rowErrors = useMemo(() => {
    if (!submitted) return {};
    const errs = {};
    rows.forEach((row, idx) => {
      const hasHours = Object.values(row.days).some(day =>
        (parseFloat(day.reg) || 0) + (parseFloat(day.t12) || 0) + (parseFloat(day.dbl) || 0) > 0
      );
      if (!hasHours) return; // blank rows are fine
      const rowErrs = validateSchema(
        { project_id: row.project_id, op_code: row.op_code },
        {
          project_id: [required('Project required')],
          op_code:    [required('Op code required')],
        }
      );
      if (!isValid(rowErrs)) errs[idx] = rowErrs;
    });
    return errs;
  }, [submitted, rows]);

  // Allowance column totals for the summary column
  const allowanceWeekTotals = {
    rnb:      dailyAllowances.reduce((s, a) => s + (parseFloat(a.rnb)      || 0), 0),
    meals:    dailyAllowances.reduce((s, a) => s + (parseFloat(a.meals)    || 0), 0),
    travel_2: dailyAllowances.reduce((s, a) => s + (parseFloat(a.travel_2) || 0), 0),
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      {/* Toast */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', padding: '1rem 1.5rem', borderRadius: '8px', backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545', color: 'white', zIndex: 1100, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          {toast.message}
        </div>
      )}

      <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '95%', maxWidth: '1400px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={goToPreviousWeek} style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer', fontSize: '1rem' }}>&lt; Prev</button>
            <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>Week of {getWeekTitle()}</h2>
            <button onClick={goToNextWeek} style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer', fontSize: '1rem' }}>Next &gt;</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {saving && <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>Saving...</span>}
            <button type="button" onClick={() => setShowHelp(true)} style={{ padding: '8px 12px', border: 'none', borderRadius: '4px', backgroundColor: '#0d9488', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>❓ Help</button>
            <button onClick={handleClose} style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', backgroundColor: '#6c757d', color: 'white', cursor: 'pointer' }}>Close</button>
          </div>
        </div>

        {/* Employee Selection */}
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #dee2e6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: '500', minWidth: '80px' }}>Employee:</label>
            {loadingCurrentUser ? (
              <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>Loading your employee profile...</div>
            ) : (
              <>
                <div style={{ flex: 1, maxWidth: '400px' }}>
                  <div style={{ border: modalErrors.employee ? '1px solid #dc3545' : 'none', borderRadius: '4px' }}>
                    <EmployeeAutocomplete
                      value={selectedEmployee} onChange={setSelectedEmployee}
                      projectId={projectId} placeholder="Search by name or code..."
                      disabled={!isElevated && selectedEmployee !== null}
                    />
                  </div>
                  {modalErrors.employee && (
                    <div style={{ color: '#dc3545', fontSize: '0.78rem', marginTop: '3px' }}>{modalErrors.employee}</div>
                  )}
                </div>
                {selectedEmployee && (
                  <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                    Code: <strong>{selectedEmployee.emp_code}</strong>
                    {selectedEmployee.trade && ` | ${selectedEmployee.trade}`}
                  </div>
                )}
                {!selectedEmployee && !loadingCurrentUser && (
                  <div style={{ backgroundColor: '#fff3cd', padding: '8px 12px', borderRadius: '4px', fontSize: '0.85rem', color: '#856404' }}>
                    ⚠️ Your email is not linked to an employee record. Please select manually or contact admin.
                  </div>
                )}
                {selectedEmployee && !isElevated && (
                  <div style={{ backgroundColor: '#d4edda', padding: '4px 10px', borderRadius: '4px', fontSize: '0.8rem', color: '#155724' }}>
                    ✓ Auto-filled from your account
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Timesheet Grid */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1rem 1.5rem' }}>
          {loading || loadingCurrentUser ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
              {loadingCurrentUser ? 'Loading your employee profile...' : 'Loading...'}
            </div>
          ) : !selectedEmployee ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
              {isElevated ? 'Please select an employee to enter time' : 'No employee record found for your account. Please contact an administrator.'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#343a40', color: 'white' }}>
                  <th style={{ padding: '10px', textAlign: 'left', width: '180px' }}>Project #</th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '200px' }}>Op Description</th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '100px' }}>Op Code</th>
                  <th style={{ padding: '6px', textAlign: 'center', width: '50px' }}>Shift</th>
                  <th style={{ padding: '10px', textAlign: 'center', width: '40px' }}>Type</th>
                  {weekDates.map((dateInfo, idx) => {
                    const fmt = formatDateHeader(dateInfo);
                    return (
                      <th key={idx} style={{ padding: '8px', textAlign: 'center', minWidth: '70px' }}>
                        <div>{fmt.day}</div>
                        <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>{fmt.month} {fmt.date}</div>
                      </th>
                    );
                  })}
                  <th style={{ padding: '6px', textAlign: 'center', width: '80px' }}>
                    <div>Total</div>
                    <div style={{ fontSize: '0.55rem', opacity: 0.7, fontWeight: 'normal' }}>Hrs (R/T/D)</div>
                  </th>
                  <th style={{ padding: '6px', textAlign: 'center', width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    {/* REG row — rowSpan covers REG, T12, DBL, HIGH, RnB, Meal$, Travel$, Comment (8 rows) */}
                    <tr style={{ backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f8f9fa' }}>
                      <td rowSpan={8} style={{ padding: '8px', borderRight: '1px solid #dee2e6', verticalAlign: 'top', outline: rowErrors[rowIndex]?.project_id ? '2px solid #dc3545' : 'none' }}>
                        <GroupedProjectSelect
                          value={row.optionKey}
                          onChange={(optionKey) => handleRowProjectChange(rowIndex, optionKey)}
                          options={projectDisciplineOptions}
                          placeholder="Select project..."
                          style={{ minWidth: '180px' }}
                        />
                        {rowErrors[rowIndex]?.project_id && (
                          <div style={{ color: '#dc3545', fontSize: '0.72rem', marginTop: '3px' }}>Required</div>
                        )}
                      </td>
                      <td rowSpan={8} style={{ padding: '8px', borderRight: '1px solid #dee2e6', verticalAlign: 'top', outline: rowErrors[rowIndex]?.op_code ? '2px solid #dc3545' : 'none' }}>
                        <OpCodeDescriptionSelect
                          value={{ op_code: row.op_code, op_description: row.op_code_description }}
                          onChange={(data) => handleOpCodeChange(rowIndex, data)}
                          jobId={row.project_id}
                          disciplineCode={row.optionKey ? row.optionKey.split('-')[1] : null}
                          displayField="op_description"
                          placeholder="Search description..."
                        />
                        {rowErrors[rowIndex]?.op_code && (
                          <div style={{ color: '#dc3545', fontSize: '0.72rem', marginTop: '3px' }}>Required</div>
                        )}
                      </td>
                      <td rowSpan={8} style={{ padding: '8px', borderRight: '1px solid #dee2e6', verticalAlign: 'top', outline: rowErrors[rowIndex]?.op_code ? '2px solid #dc3545' : 'none' }}>
                        <OpCodeDescriptionSelect
                          value={{ op_code: row.op_code, op_description: row.op_code_description }}
                          onChange={(data) => handleOpCodeChange(rowIndex, data)}
                          jobId={row.project_id}
                          disciplineCode={row.optionKey ? row.optionKey.split('-')[1] : null}
                          displayField="op_code"
                          placeholder="Search code..."
                        />
                      </td>
                      <td rowSpan={8} style={{ padding: '4px', borderRight: '1px solid #dee2e6', verticalAlign: 'top', textAlign: 'center' }}>
                        <select
                          value={row.shift}
                          onChange={(e) => handleRowShiftChange(rowIndex, e.target.value)}
                          style={{ width: '40px', marginTop: '5px', padding: '6px 2px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.85rem', fontWeight: '600', boxSizing: 'border-box', backgroundColor: row.shift === 'N' ? '#f3e8ff' : row.shift === 'A' ? '#fff8e1' : 'white', color: row.shift === 'N' ? '#6f42c1' : row.shift === 'A' ? '#e65100' : '#333', cursor: 'pointer', textAlign: 'center', textAlignLast: 'center', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                          title="Default shift: D=Day (0), A=Afternoon (1), N=Night (2)"
                        >
                          <option value="D">D</option>
                          <option value="A">A</option>
                          <option value="N">N</option>
                        </select>
                        <div style={{ fontSize: '0.6rem', color: '#6c757d', marginTop: '2px' }}>Day badge overrides</div>
                      </td>
                      <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.75rem', color: '#28a745', fontWeight: '500' }}>REG</td>
                      {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
                        const hasHours       = dayHasHours(row.days[dayIndex]);
                        const effectiveShift = getEffectiveShift(row, dayIndex);
                        const isOverridden   = row.days[dayIndex].shift !== null;
                        return (
                          <td key={dayIndex} style={{ padding: '2px', position: 'relative' }}>
                            <input type="number" step="0.5" min="0" max="24"
                              value={row.days[dayIndex].reg}
                              onChange={(e) => handleCellChange(rowIndex, dayIndex, 'reg', e.target.value)}
                              style={{ width: '100%', padding: '6px', border: '1px solid #dee2e6', borderRadius: '3px', textAlign: 'center', fontSize: '0.85rem', boxSizing: 'border-box' }}
                            />
                            {hasHours && (
                              <button
                                onClick={() => toggleDayShift(rowIndex, dayIndex)}
                                title={isOverridden ? `Override: ${effectiveShift === 'D' ? 'Day' : effectiveShift === 'A' ? 'Afternoon' : 'Night'} (click to cycle)` : `Row default: ${effectiveShift === 'D' ? 'Day' : effectiveShift === 'A' ? 'Afternoon' : 'Night'} (click to override)`}
                                style={{ position: 'absolute', top: '2px', right: '4px', padding: '1px 4px', fontSize: '0.55rem', fontWeight: '600', border: 'none', borderRadius: '3px', cursor: 'pointer', backgroundColor: isOverridden ? (effectiveShift === 'D' ? '#ffc107' : effectiveShift === 'A' ? '#e65100' : '#6f42c1') : (effectiveShift === 'D' ? '#e9ecef' : effectiveShift === 'A' ? '#fff3e0' : '#e2d9f3'), color: isOverridden ? 'white' : (effectiveShift === 'D' ? '#6c757d' : effectiveShift === 'A' ? '#e65100' : '#6f42c1'), boxShadow: isOverridden ? '0 1px 2px rgba(0,0,0,0.2)' : 'none' }}
                              >
                                {effectiveShift}
                              </button>
                            )}
                          </td>
                        );
                      })}
                      <td rowSpan={8} style={{ padding: '4px', textAlign: 'center', borderLeft: '1px solid #dee2e6', fontSize: '0.7rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                          <div style={{ color: '#28a745' }}>R: {getRowTotalByType(row, 'reg').toFixed(1)}</div>
                          <div style={{ color: '#ffc107' }}>T: {getRowTotalByType(row, 't12').toFixed(1)}</div>
                          <div style={{ color: '#dc3545' }}>D: {getRowTotalByType(row, 'dbl').toFixed(1)}</div>
                          <div style={{ color: '#fd7e14' }}>H: {getRowTotalByType(row, 'height').toFixed(1)}</div>
                          <div style={{ fontWeight: '700', borderTop: '1px solid #dee2e6', paddingTop: '2px', marginTop: '2px' }}>{getRowTotal(row)}</div>
                        </div>
                      </td>
                      <td rowSpan={8} style={{ padding: '4px', textAlign: 'center', verticalAlign: 'middle' }}>
                        {rows.length > 1 && (
                          <button onClick={() => removeRow(rowIndex)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '1.2rem', padding: '4px', lineHeight: 1 }} title="Remove row">×</button>
                        )}
                      </td>
                    </tr>
                    {/* T12 Row */}
                    <tr style={{ backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f8f9fa' }}>
                      <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.75rem', color: '#ffc107', fontWeight: '500' }}>T12</td>
                      {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                        <td key={dayIndex} style={{ padding: '2px' }}>
                          <input type="number" step="0.5" min="0" max="24"
                            value={row.days[dayIndex].t12}
                            onChange={(e) => handleCellChange(rowIndex, dayIndex, 't12', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #dee2e6', borderRadius: '3px', textAlign: 'center', fontSize: '0.85rem', boxSizing: 'border-box' }}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* DBL Row */}
                    <tr style={{ backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f8f9fa' }}>
                      <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.75rem', color: '#dc3545', fontWeight: '500' }}>DBL</td>
                      {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                        <td key={dayIndex} style={{ padding: '2px' }}>
                          <input type="number" step="0.5" min="0" max="24"
                            value={row.days[dayIndex].dbl}
                            onChange={(e) => handleCellChange(rowIndex, dayIndex, 'dbl', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #dee2e6', borderRadius: '3px', textAlign: 'center', fontSize: '0.85rem', boxSizing: 'border-box' }}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* Height Row */}
                    <tr style={{ backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f8f9fa' }}>
                      <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.75rem', color: '#fd7e14', fontWeight: '500' }} title="Height Pay">HIGH</td>
                      {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                        <td key={dayIndex} style={{ padding: '2px' }}>
                          <input type="number" step="0.5" min="0" max="24"
                            value={row.days[dayIndex].height}
                            onChange={(e) => handleCellChange(rowIndex, dayIndex, 'height', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #dee2e6', borderRadius: '3px', textAlign: 'center', fontSize: '0.85rem', boxSizing: 'border-box' }}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* RnB computed row (read-only) */}
                    <tr style={{ backgroundColor: rowIndex % 2 === 0 ? '#f0fbfd' : '#e6f7fb' }}>
                      <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.72rem', color: '#17a2b8', fontWeight: '500', fontStyle: 'italic' }}>RnB $</td>
                      {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
                        const val = computedRowAllowances[dayIndex]?.[rowIndex];
                        return (
                          <td key={dayIndex} style={{ padding: '2px' }}>
                            <div style={{ width: '100%', padding: '5px 4px', backgroundColor: val !== undefined ? '#e0f7fa' : '#f5f5f5', border: '1px solid #b2d8d8', borderRadius: '3px', textAlign: 'center', fontSize: '0.8rem', color: val !== undefined ? '#0c7a8e' : '#bbb', fontStyle: 'italic', boxSizing: 'border-box' }}>
                              {val !== undefined ? val.rnb.toFixed(2) : '—'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {/* Meal$ computed row (read-only) */}
                    <tr style={{ backgroundColor: rowIndex % 2 === 0 ? '#f0fdf8' : '#e6fbf3' }}>
                      <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.72rem', color: '#20c997', fontWeight: '500', fontStyle: 'italic' }}>Meal $</td>
                      {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
                        const val = computedRowAllowances[dayIndex]?.[rowIndex];
                        return (
                          <td key={dayIndex} style={{ padding: '2px' }}>
                            <div style={{ width: '100%', padding: '5px 4px', backgroundColor: val !== undefined ? '#e0faf3' : '#f5f5f5', border: '1px solid #b2dfdb', borderRadius: '3px', textAlign: 'center', fontSize: '0.8rem', color: val !== undefined ? '#147a58' : '#bbb', fontStyle: 'italic', boxSizing: 'border-box' }}>
                              {val !== undefined ? val.meals.toFixed(2) : '—'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {/* Travel$ computed row (read-only) */}
                    <tr style={{ backgroundColor: rowIndex % 2 === 0 ? '#fdf0f7' : '#fae6f1', borderBottom: '2px solid #dee2e6' }}>
                      <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.72rem', color: '#e83e8c', fontWeight: '500', fontStyle: 'italic' }}>Travel $</td>
                      {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
                        const val = computedRowAllowances[dayIndex]?.[rowIndex];
                        return (
                          <td key={dayIndex} style={{ padding: '2px' }}>
                            <div style={{ width: '100%', padding: '5px 4px', backgroundColor: val !== undefined ? '#fce4f3' : '#f5f5f5', border: '1px solid #f8bbd0', borderRadius: '3px', textAlign: 'center', fontSize: '0.8rem', color: val !== undefined ? '#9c1556' : '#bbb', fontStyle: 'italic', boxSizing: 'border-box' }}>
                              {val !== undefined ? val.travel_2.toFixed(2) : '—'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {/* Comment sub-row — one text input per day */}
                    <tr style={{ backgroundColor: rowIndex % 2 === 0 ? '#fffdf0' : '#fefce8', borderBottom: '2px solid #dee2e6' }}>
                      <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.72rem', color: '#92400e', fontWeight: '500', fontStyle: 'italic' }}>Comment</td>
                      {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                        <td key={dayIndex} style={{ padding: '2px' }}>
                          <input
                            type="text"
                            maxLength={200}
                            value={row.days[dayIndex]?.comment || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setRows(prev => prev.map(r => r === row
                                ? { ...r, days: { ...r.days, [dayIndex]: { ...r.days[dayIndex], comment: val } } }
                                : r
                              ));
                            }}
                            placeholder="…"
                            style={{
                              width: '100%', padding: '4px 5px',
                              border: '1px solid #fde68a', borderRadius: '3px',
                              fontSize: '0.75rem', backgroundColor: '#fffff8',
                              boxSizing: 'border-box', color: '#78350f',
                            }}
                          />
                        </td>
                      ))}
                      <td /><td />
                    </tr>
                  </React.Fragment>
                ))}

                {/* Daily Allowances Section — one set of inputs spanning all 7 days */}
                <tr style={{ backgroundColor: '#e8f4f8', borderTop: '2px solid #17a2b8' }}>
                  <td colSpan={5} style={{ padding: '6px 12px', fontWeight: '600', color: '#0c6b8c', fontSize: '0.8rem' }}>
                    <span style={{ marginRight: '12px' }}>Daily Allowances</span>
                    <button
                      onClick={handleCalculateAllowances}
                      style={{ padding: '5px 14px', border: 'none', borderRadius: '4px', backgroundColor: '#17a2b8', color: 'white', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '700', verticalAlign: 'middle' }}
                      title="Distribute allowances proportionally across project rows by hours for all 7 days"
                    >
                      Calculate
                    </button>
                    <span style={{ fontWeight: '400', color: '#5a9bb0', marginLeft: '10px', fontSize: '0.72rem' }}>
                      — enter per-day totals then click Calculate to preview distribution in project rows above
                    </span>
                  </td>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                    <td key={dayIndex} style={{ padding: '2px', backgroundColor: '#e8f4f8' }}></td>
                  ))}
                  <td style={{ backgroundColor: '#e8f4f8' }}></td>
                  <td style={{ backgroundColor: '#e8f4f8' }}></td>
                </tr>
                {/* RnB Row */}
                <tr style={{ backgroundColor: '#f0fbff' }}>
                  <td colSpan={5} style={{ padding: '4px 12px', color: '#17a2b8', fontSize: '0.8rem', fontWeight: '500' }}>RnB $</td>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                    <td key={dayIndex} style={{ padding: '2px' }}>
                      <input type="number" step="0.01" min="0"
                        value={dailyAllowances[dayIndex]?.rnb || ''}
                        onChange={(e) => updateDailyAllowance(dayIndex, 'rnb', e.target.value)}
                        placeholder="0.00"
                        style={{ width: '100%', padding: '6px', border: '1px solid #b2d8d8', borderRadius: '3px', textAlign: 'center', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                    </td>
                  ))}
                  <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.7rem', color: '#17a2b8', fontWeight: '600' }}>
                    {allowanceWeekTotals.rnb.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
                {/* Meal$ Row */}
                <tr style={{ backgroundColor: '#f0fbff' }}>
                  <td colSpan={5} style={{ padding: '4px 12px', color: '#20c997', fontSize: '0.8rem', fontWeight: '500' }}>Meal $</td>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                    <td key={dayIndex} style={{ padding: '2px' }}>
                      <input type="number" step="0.01" min="0"
                        value={dailyAllowances[dayIndex]?.meals || ''}
                        onChange={(e) => updateDailyAllowance(dayIndex, 'meals', e.target.value)}
                        placeholder="0.00"
                        style={{ width: '100%', padding: '6px', border: '1px solid #b2dfdb', borderRadius: '3px', textAlign: 'center', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                    </td>
                  ))}
                  <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.7rem', color: '#20c997', fontWeight: '600' }}>
                    {allowanceWeekTotals.meals.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
                {/* Travel$ Row */}
                <tr style={{ backgroundColor: '#f0fbff', borderBottom: '2px solid #dee2e6' }}>
                  <td colSpan={5} style={{ padding: '4px 12px', color: '#e83e8c', fontSize: '0.8rem', fontWeight: '500' }}>Travel $</td>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                    <td key={dayIndex} style={{ padding: '2px' }}>
                      <input type="number" step="0.01" min="0"
                        value={dailyAllowances[dayIndex]?.travel_2 || ''}
                        onChange={(e) => updateDailyAllowance(dayIndex, 'travel_2', e.target.value)}
                        placeholder="0.00"
                        style={{ width: '100%', padding: '6px', border: '1px solid #f8bbd0', borderRadius: '3px', textAlign: 'center', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                    </td>
                  ))}
                  <td style={{ padding: '4px', textAlign: 'center', fontSize: '0.7rem', color: '#e83e8c', fontWeight: '600' }}>
                    {allowanceWeekTotals.travel_2.toFixed(2)}
                  </td>
                  <td></td>
                </tr>

                {/* Day Totals Row */}
                <tr style={{ backgroundColor: '#e9ecef', fontWeight: '600' }}>
                  <td colSpan={5} style={{ padding: '6px', textAlign: 'right', verticalAlign: 'top' }}>Daily Totals:</td>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                    <td key={dayIndex} style={{ padding: '6px', textAlign: 'center', fontSize: '0.7rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        <div style={{ color: '#28a745' }}>R: {getDayTotalByType(dayIndex, 'reg').toFixed(1)}</div>
                        <div style={{ color: '#b8860b' }}>T: {getDayTotalByType(dayIndex, 't12').toFixed(1)}</div>
                        <div style={{ color: '#dc3545' }}>D: {getDayTotalByType(dayIndex, 'dbl').toFixed(1)}</div>
                        <div style={{ color: '#fd7e14' }}>H: {getDayTotalByType(dayIndex, 'height').toFixed(1)}</div>
                        <div style={{ color: '#17a2b8' }}>RnB: {parseFloat(dailyAllowances[dayIndex]?.rnb || 0).toFixed(2)}</div>
                        <div style={{ color: '#20c997' }}>Meal$: {parseFloat(dailyAllowances[dayIndex]?.meals || 0).toFixed(2)}</div>
                        <div style={{ color: '#e83e8c' }}>Trv$: {parseFloat(dailyAllowances[dayIndex]?.travel_2 || 0).toFixed(2)}</div>
                        <div style={{ fontWeight: '700', borderTop: '1px solid #ccc', paddingTop: '2px', marginTop: '2px' }}>{getDayTotal(dayIndex)}</div>
                      </div>
                    </td>
                  ))}
                  <td style={{ padding: '6px', textAlign: 'center', backgroundColor: '#28a745', color: 'white', fontSize: '0.7rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      <div>R: {getGrandTotalByType('reg').toFixed(1)}</div>
                      <div>T: {getGrandTotalByType('t12').toFixed(1)}</div>
                      <div>D: {getGrandTotalByType('dbl').toFixed(1)}</div>
                      <div>H: {getGrandTotalByType('height').toFixed(1)}</div>
                      <div>RnB: {allowanceWeekTotals.rnb.toFixed(2)}</div>
                      <div>Meal$: {allowanceWeekTotals.meals.toFixed(2)}</div>
                      <div>Trv$: {allowanceWeekTotals.travel_2.toFixed(2)}</div>
                      <div style={{ fontWeight: '700', borderTop: '1px solid rgba(255,255,255,0.5)', paddingTop: '2px', marginTop: '2px' }}>
                        {rows.reduce((sum, row) => sum + parseFloat(getRowTotal(row)), 0).toFixed(1)}
                      </div>
                    </div>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
          {/* Validation summary banner */}
          {submitted && Object.keys(rowErrors).length > 0 && (
            <div style={{
              padding: '0.6rem 1.5rem',
              backgroundColor: '#fee2e2', color: '#991b1b',
              fontSize: '0.82rem', borderBottom: '1px solid #fecaca',
              display: 'flex', alignItems: 'center', gap: '0.4rem'
            }}>
              <span style={{ fontWeight: '600' }}>⚠</span>
              {Object.keys(rowErrors).length} row(s) are missing required fields — check highlighted cells above
            </div>
          )}
          <div style={{ padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={addRow} disabled={!selectedEmployee}
                style={{ padding: '8px 16px', border: '1px solid #17a2b8', borderRadius: '4px', backgroundColor: 'white', color: '#17a2b8', cursor: selectedEmployee ? 'pointer' : 'not-allowed', fontSize: '0.9rem', opacity: selectedEmployee ? 1 : 0.5 }}
              >
                + Add Row
              </button>
              <button
                onClick={handleCopyLastWeek}
                disabled={!selectedEmployee || copyingLastWeek}
                title="Copy project/op-code rows from last week with zero hours"
                style={{ padding: '8px 16px', border: '1px solid #6c757d', borderRadius: '4px', backgroundColor: 'white', color: '#6c757d', cursor: (selectedEmployee && !copyingLastWeek) ? 'pointer' : 'not-allowed', fontSize: '0.9rem', opacity: selectedEmployee ? 1 : 0.5 }}
              >
                {copyingLastWeek ? 'Copying...' : '📋 Copy Last Week'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <button
                onClick={() => { isDirtyRef.current = false; autoSave(); }}
                disabled={!selectedEmployee || saving}
                style={{ padding: '10px 24px', border: '1px solid #6c757d', borderRadius: '4px', backgroundColor: 'white', color: '#6c757d', cursor: selectedEmployee && !saving ? 'pointer' : 'not-allowed', fontSize: '0.9rem', opacity: selectedEmployee ? 1 : 0.5 }}
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handleSubmit} disabled={submitting}
                style={{ padding: '10px 24px', border: 'none', borderRadius: '4px', backgroundColor: submitting ? '#6c757d' : '#28a745', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: '600' }}
              >
                {submitting ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showHelp && <TimeTrackerHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

export default WeeklyTimesheetModal;
